// Coach CRM list (/coaches): the concierges' replacement for the coach spreadsheet. Dense and filterable;
// a row opens the coach (/coaches/:id), the checkboxes feed the batch Text flow.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCatalog } from '../context/CatalogContext';
import { useCrm } from '../lib/crm';
import { AdminSidebar } from '../components/AdminShell';
import CollegePicker from '../components/CollegePicker';
import TemplatesManager from '../components/crm/TemplatesManager';
import ReassignPanel from '../components/crm/ReassignPanel';
import './AdminDashboard.css';
import '../components/AdminShell.css';
import './CoachesPage.css';

export const STATUS_LABELS = { active: 'Active', paused: 'Paused', do_not_contact: 'Do not contact' };
const SEARCH_DELAY_MS = 250;

export const coachName = (c) => [c.first_name, c.last_name].filter(Boolean).join(' ');

// US numbers read as (513) 555-0100; anything else is shown as stored.
export function formatPhone(p) {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(p || '');
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : (p || '');
}

// Column sort for the list. Name sorts by last name, then first; blanks always sort last.
// Last activity compares the server's ISO timestamps as strings (same format, so string order is
// time order); a coach with no activity is a blank and sorts last.
const SORT_KEYS = {
  name: (r) => [r.coach?.last_name, r.coach?.first_name].filter(Boolean).join(' '),
  school: (r) => r.coach?.school || '',
  title: (r) => r.coach?.title || '',
  activity: (r) => r.last_activity_at || '',
};
// The direction a column sorts in on its first click. Activity starts newest-first.
const FIRST_DIR = { activity: 'desc' };
export function sortRows(rows, sort) {
  const keyOf = sort && SORT_KEYS[sort.key];
  if (!keyOf) return rows;
  const dir = sort.dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const x = keyOf(a).trim(); const y = keyOf(b).trim();
    if (!x || !y) return (!x) - (!y);
    return dir * x.localeCompare(y, undefined, { sensitivity: 'base' });
  });
}

// The list's current order, for the coach page's Next button. Session storage: per tab, gone on close.
export const ORDER_KEY = 'crm.coachOrder';
const SORT_STORE_KEY = 'crm.coachSort';
const readStore = (k) => { try { return JSON.parse(sessionStorage.getItem(k)); } catch { return null; } };
const writeStore = (k, v) => { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch { /* storage off */ } };

export function timeAgo(iso, now = Date.now()) {
  if (!iso) return '';
  const days = Math.floor((now - new Date(iso).getTime()) / 86400000);
  if (days < 1) return 'Today';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

const EMPTY_FORM = { first_name: '', last_name: '', school: '', hs_class: '', unitid: null, sport_slug: '', title: '', phone: '', email: '', twitter: '' };

function AddCoachModal({ sports, kind, onClose, onSaved }) {
  const isHs = kind === 'high_school';
  const crm = useCrm();
  // Default to the catalog's first sport; the literal fallback only covers an empty catalog.
  const [form, setForm] = useState({ ...EMPTY_FORM, sport_slug: sports[0]?.slug || 'football' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const canSave = !!form.last_name.trim() && !saving && (!isHs || !!form.school.trim());
  const save = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    const text = (v) => v.trim() || undefined;
    const school = form.school.trim();
    const body = {
      coach: {
        first_name: text(form.first_name), last_name: form.last_name.trim(), sport_slug: form.sport_slug,
        title: text(form.title),
        ...(isHs
          // A high school is free text, never a unitid; the server requires the name.
          ? { kind: 'high_school', school_name: school, hs_classification: text(form.hs_class) }
          // A picked college sends its unitid; typed text with no match is kept as the school name.
          : (form.unitid != null ? { unitid: form.unitid } : (school ? { school_name: school } : {}))),
      },
      phone: text(form.phone), email: text(form.email), twitter: text(form.twitter),
    };
    setSaving(true); setError('');
    try {
      const rel = await crm.createRelationship(body);
      onSaved(rel.id, null);
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.relationship_id) {
        onSaved(err.response.data.relationship_id, 'You already have this coach');
      } else {
        const d = err.response?.data?.detail;
        setError(d === 'school_required' ? 'Enter the high school.' : d === 'unknown_school' ? 'That school is not in our list. Clear it or pick another.'
          : (typeof d === 'string' ? d : 'Could not add that coach.'));
        setSaving(false);
      }
    }
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="add-coach-title" onClick={onClose}>
      <form className="modal-panel cr-modal" onClick={(e) => e.stopPropagation()} onSubmit={save}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        <h2 id="add-coach-title" className="cr-modal__title">{isHs ? 'Add high school coach' : 'Add coach'}</h2>
        <div className="cr-form">
          <label className="adm-field">
            <span className="adm-field__label">First name</span>
            <input className="adm-input" value={form.first_name} onChange={set('first_name')} data-testid="add_coach_first" autoFocus />
          </label>
          <label className="adm-field">
            <span className="adm-field__label">Last name</span>
            <input className="adm-input" value={form.last_name} onChange={set('last_name')} data-testid="add_coach_last" required />
          </label>
          {isHs ? (
            <>
              <label className="adm-field">
                <span className="adm-field__label">High school</span>
                <input className="adm-input" value={form.school} onChange={set('school')} data-testid="add_coach_high_school" required />
              </label>
              <label className="adm-field">
                <span className="adm-field__label">Class</span>
                <input className="adm-input" value={form.hs_class} onChange={set('hs_class')} placeholder="6A" data-testid="add_coach_class" />
              </label>
            </>
          ) : (
          <div className="adm-field cr-form__wide">
            <span className="adm-field__label">School</span>
            <CollegePicker
              value={form.school}
              onInputChange={(t) => setForm((f) => ({ ...f, school: t, unitid: null }))}
              onSelect={(c) => setForm((f) => ({ ...f, school: c.n, unitid: c.u }))}
              className="adm-input"
              placeholder="Start typing, or type a school we do not list"
            />
          </div>
          )}
          <label className="adm-field">
            <span className="adm-field__label">Sport</span>
            <select className="adm-select" value={form.sport_slug} onChange={set('sport_slug')} data-testid="add_coach_sport">
              {sports.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
            </select>
          </label>
          <label className="adm-field">
            <span className="adm-field__label">Title</span>
            <input className="adm-input" value={form.title} onChange={set('title')} placeholder="Head coach" data-testid="add_coach_title" />
          </label>
          <label className="adm-field">
            <span className="adm-field__label">Phone</span>
            <input className="adm-input" type="tel" value={form.phone} onChange={set('phone')} data-testid="add_coach_phone" />
          </label>
          <label className="adm-field">
            <span className="adm-field__label">Email</span>
            <input className="adm-input" type="email" value={form.email} onChange={set('email')} data-testid="add_coach_email" />
          </label>
          <label className="adm-field cr-form__wide">
            <span className="adm-field__label">Twitter</span>
            <input className="adm-input" value={form.twitter} onChange={set('twitter')} placeholder="@handle" data-testid="add_coach_twitter" />
          </label>
        </div>
        {error && <div role="alert" className="cr-error">{error}</div>}
        <div className="cr-modal__actions">
          <button type="button" className="adm-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="adm-btn adm-btn--primary" disabled={!canSave} data-testid="add_coach_save">
            {saving ? 'Adding…' : (isHs ? 'Add high school coach' : 'Add coach')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function CoachesPage({ kind = 'college' }) {
  const isHs = kind === 'high_school';
  const { user } = useAuth();
  const { sports } = useCatalog();
  const crm = useCrm();
  const navigate = useNavigate();
  const location = useLocation();
  const isStaff = user?.role !== 'partner';

  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [division, setDivision] = useState('');
  const [status, setStatus] = useState('');
  const [scope, setScope] = useState('all');
  const [sort, setSort] = useState(() => readStore(SORT_STORE_KEY));
  const [selected, setSelected] = useState(() => new Set());
  // Divisions come from the rows seen so far (a union, so filtering by one does not hide the others).
  const [divisions, setDivisions] = useState([]);
  const [adding, setAdding] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [notice, setNotice] = useState(location.state?.notice || '');
  const reqId = useRef(0);
  const firstLoad = useRef(true);

  // Debounced fetch; a stale response never overwrites a newer one.
  useEffect(() => {
    const id = ++reqId.current;
    const timer = setTimeout(() => {
      const params = {};
      if (isHs) params.kind = 'high_school';
      if (search.trim()) params.q = search.trim();
      if (division) params[isHs ? 'classification' : 'division'] = division;
      if (status) params.status = status;
      if (isStaff && scope !== 'all') params.shared = scope;
      crm.listRelationships(params)
        .then((data) => {
          if (id !== reqId.current) return;
          setRows(data); setError('');
          const seen = data.map((r) => (isHs ? r.classification : r.coach?.division)).filter(Boolean);
          setDivisions((d) => (seen.every((x) => d.includes(x)) ? d : [...new Set([...d, ...seen])].sort()));
        })
        .catch(() => { if (id === reqId.current) setError('Could not load coaches.'); })
        .finally(() => { if (id === reqId.current) setLoaded(true); });
    }, firstLoad.current ? 0 : SEARCH_DELAY_MS);
    firstLoad.current = false;
    return () => clearTimeout(timer);
  }, [crm, isHs, search, division, status, scope, isStaff]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(''), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  const toggle = useCallback((id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  }), []);
  const sorted = useMemo(() => sortRows(rows, sort), [rows, sort]);
  const sortBy = (key) => setSort((cur) => {
    const next = cur?.key === key ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: FIRST_DIR[key] || 'asc' };
    writeStore(SORT_STORE_KEY, next);
    return next;
  });
  const openCoach = (id) => { writeStore(ORDER_KEY, sorted.map((r) => r.id)); navigate(`/coaches/${id}`); };
  const sortHead = (key, label) => {
    const on = sort?.key === key;
    return (
      <th aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
        <button type="button" className={`cr-sort${on ? ' cr-sort--on' : ''}`} onClick={() => sortBy(key)} data-testid={`coaches_sort_${key}`}>
          {label}<span className="cr-sort__arrow" aria-hidden="true">{on ? (sort.dir === 'asc' ? '▲' : '▼') : ''}</span>
        </button>
      </th>
    );
  };
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));
  const visibleSelected = rows.filter((r) => selected.has(r.id)).map((r) => r.id);

  const saved = (id, msg) => navigate(`/coaches/${id}`, msg ? { state: { notice: msg } } : undefined);
  const divisionOptions = division && !divisions.includes(division) ? [...divisions, division].sort() : divisions;
  const filtered = !!(search.trim() || division || status || scope !== 'all');

  return (
    <div className="adm">
      <div className="adm__shell">
        <AdminSidebar active={isHs ? 'hs-coaches' : 'coaches'} />
        <main className="adm__main">
          {notice && <div className="settings-toast" role="status" data-testid="coaches_notice">{notice}</div>}
          {error && <div role="alert" className="settings-toast settings-toast--error">{error}</div>}
          <div className="adm__card adm__card--flush">
            <div className="cr-head">
              <h1 className="cr-head__title">{isHs ? 'High School Coaches' : 'College Coaches'}</h1>
              <span className="cr-head__count" data-testid="coaches_count">{loaded ? `${rows.length} ${rows.length === 1 ? 'coach' : 'coaches'}` : ''}</span>
              <button type="button" className="adm-btn cr-head__add" onClick={() => setTemplatesOpen(true)} data-testid="coaches_templates">Templates</button>
              <button type="button" className="adm-btn adm-btn--primary" onClick={() => setAdding(true)} data-testid="coaches_add">
                {isHs ? '+ Add high school coach' : '+ Add coach'}
              </button>
            </div>
            {/* Partners kept confusing their own staff with college coaches (nav redesign, 2026-11-05). */}
            {isHs && (
              <p className="cr-intro" data-testid="coaches_hs_intro">
                Head coaches and athletic staff at <strong>high schools</strong> — the people who know your athletes and can vouch for them.
                {' '}Looking for college recruiters? They&apos;re under <Link className="adm-link" to="/coaches">College Coaches</Link>.
              </p>
            )}
            {!isHs && !isStaff && (
              <p className="cr-intro" data-testid="coaches_intro">
                Coaches in your own program are under <Link className="adm-link" to="/partner/team">Athletes &amp; Staff</Link>.
              </p>
            )}
            <div className="adm-toolbar adm-toolbar--incard cr-toolbar">
              <input className="adm-input cr-search" type="search" placeholder="Search name or school" aria-label="Search coaches"
                value={search} onChange={(e) => setSearch(e.target.value)} data-testid="coaches_search" />
              <select className="adm-select" aria-label={isHs ? 'Classification' : 'Division'} value={division} onChange={(e) => setDivision(e.target.value)} data-testid="coaches_division">
                <option value="">{isHs ? 'All classifications' : 'All divisions'}</option>
                {divisionOptions.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <select className="adm-select" aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="coaches_status">
                <option value="">Any status</option>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              {isStaff && (
                <div className="cr-seg" role="group" aria-label="Whose coaches">
                  {[['all', 'All'], ['mine', 'Mine'], ['shared', 'Shared with me']].map(([k, label]) => (
                    <button key={k} type="button" aria-pressed={scope === k} data-testid={`coaches_scope_${k}`}
                      className={`cr-seg__btn${scope === k ? ' cr-seg__btn--on' : ''}`} onClick={() => setScope(k)}>{label}</button>
                  ))}
                </div>
              )}
            </div>
            {visibleSelected.length > 0 && (
              <div className="cr-selbar" data-testid="coaches_selection">
                <span>{visibleSelected.length} selected</span>
                <button type="button" className="adm-btn adm-btn--dark adm-btn--sm" data-testid="coaches_text_selected"
                  onClick={() => navigate('/coaches/text', { state: { ids: visibleSelected, kind } })}>Text selected</button>
                <button type="button" className="adm-btn adm-btn--sm" onClick={() => setSelected(new Set())}>Clear</button>
              </div>
            )}
            <div className="adm-tablewrap" data-testid="coaches_tablewrap">
            <table className="adm-table cr-table">
              <thead>
                <tr>
                  <th className="cr-col-check"><input type="checkbox" aria-label="Select all" checked={allSelected} onChange={toggleAll} /></th>
                  {sortHead('name', 'Name')}{sortHead('school', isHs ? 'High School' : 'School')}<th>{isHs ? 'Class' : 'Div'}</th>{sortHead('title', 'Title')}<th>Phone</th>{sortHead('activity', 'Last activity')}<th>Needs</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.id} className="cr-row" data-testid={`coach_row_${r.id}`} onClick={() => openCoach(r.id)}>
                    <td className="cr-col-check" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" aria-label={`Select ${coachName(r.coach)}`} checked={selected.has(r.id)}
                        onChange={() => toggle(r.id)} data-testid={`coach_select_${r.id}`} />
                    </td>
                    <td>
                      <span className="adm-cell__name">{coachName(r.coach)}</span>
                      {r.status && r.status !== 'active' && <span className="adm-chip cr-chip">{STATUS_LABELS[r.status] || r.status}</span>}
                      {r.shared_by && <span className="cr-sub">Shared by {r.shared_by}</span>}
                    </td>
                    <td><span className="cr-clip">{r.coach.school}</span></td>
                    <td>{isHs ? r.classification : r.coach.division}</td>
                    <td><span className="cr-clip">{r.coach.title}</span></td>
                    <td className="cr-nowrap">{formatPhone(r.phone)}</td>
                    <td className="cr-nowrap">{timeAgo(r.last_activity_at)}</td>
                    <td><span className="cr-clip cr-clip--needs" title={r.needs || undefined}>{r.needs}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {user?.role === 'admin' && <ReassignPanel />}
            {loaded && rows.length === 0 && (
              <p className="cr-empty" data-testid="coaches_empty">
                {filtered ? 'No coaches match those filters.' : 'No coaches yet. Add your first coach to start your list.'}
              </p>
            )}
          </div>
        </main>
      </div>
      {templatesOpen && <TemplatesManager onClose={() => setTemplatesOpen(false)} />}
      {adding && <AddCoachModal kind={kind} sports={sports?.length ? sports : [{ slug: 'football', name: 'Football' }]} onClose={() => setAdding(false)} onSaved={saved} />}
    </div>
  );
}
